import { forwardRef, memo, useMemo, useCallback } from 'react';
import React from 'react';
import { classNames } from '../../utils/classNames';
import { BaseCommentBody } from '../BaseCommentBody';
import { ReplyComponent } from '../ReplyComponent';
import { ReplyInput } from '../ReplyInput/ReplyInput';
import type { CommentCardProps } from './CommentCard.types';
import './CommentCard.css';
import './CommentSkeleton.css';
import type {
  CommentMention,
  CommentAttachment
} from '../../containers/CommentList/CommentList.types';

const CommentCardComponent = forwardRef<HTMLDivElement, CommentCardProps>(
  (
    {
      comment,
      loggedInUserEmail = '',
      menuActions,
      editActions,
      replyActions,
      isHighlighted = false,
      styleOverrides,
      className,
      inputConfig,
      iconOverrides,
      sectionDataKey = 'sectionCategory',
      commentOnClickHandler,
      showEditedAt,
      submitOnBlur = false,
      ...restProps
    },
    ref
  ) => {
    const {
      getCommentOptions,
      getReplyOptions,
      onCommentAction,
      onReplyAction
    } = menuActions;
    const { onTextEdit, onEditStart, onEditCancel, isEditing } = editActions;
    const {
      allowReplies,
      replyValue,
      onReplyChange,
      onReplySubmit,
      onClearReply
    } = replyActions;

    const commentHandlers = useMemo(() => {
      return {
        onTextEdit: (
          newText: string,
          mentions?: CommentMention[],
          attachments?: (CommentAttachment | File)[]
        ) => onTextEdit(comment.id, newText, mentions, attachments),
        onEditStart: () => onEditStart(comment.id),
        onEditComplete: (finalText: string, wasChanged: boolean) =>
          onEditCancel(comment.id),
        onEditCancel: () => onEditCancel(comment.id)
      } as const;
    }, [comment.id, onEditCancel, onEditStart, onTextEdit]);

    const handleMenuOptionClick = useCallback(
      (optionId: string) => onCommentAction(comment.id, optionId),
      [comment.id, onCommentAction]
    );

    const handleReplyChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onReplyChange(comment.id, e.target.value),
      [comment.id, onReplyChange]
    );

    const handleReplySubmit = useCallback(
      (value: string, mentions?: CommentMention[], attachments?: File[]) =>
        onReplySubmit(comment.id, value, mentions, attachments),
      [comment.id, onReplySubmit]
    );

    // Check if this comment is in skeleton state
    const isSkeleton = (comment as any).isSkeleton === true;
    const isEditSkeleton = (comment as any).isEditSkeleton === true;

    return (
      <div
        ref={ref}
        data-comment-id={comment.id}
        className={classNames(
          'comment-card',
          isHighlighted && 'highlight',
          isSkeleton && 'comment-card--skeleton',
          className
        )}
        style={styleOverrides}
        onClick={
          commentOnClickHandler && (() => commentOnClickHandler(comment.id))
        }
        {...restProps}
      >
        {' '}
        <BaseCommentBody
          commentAuthor={comment.commentAuthor}
          currentUserEmail={loggedInUserEmail}
          text={comment.text}
          commentMentions={comment.commentMentions || []}
          createdAt={comment.createdAt}
          editedAt={comment.editedAt}
          section={comment[sectionDataKey as keyof typeof comment] as string}
          isEditing={isEditing(comment.id)}
          isEditSkeleton={isEditSkeleton}
          authorEmail={comment.commentAuthor.email}
          menuOptions={getCommentOptions(comment)}
          onMenuOptionClick={handleMenuOptionClick}
          {...commentHandlers}
          inputConfig={inputConfig}
          iconOverrides={iconOverrides}
          showEditedAt={showEditedAt}
          submitOnBlur={submitOnBlur}
          attachments={comment.attachments}
          communicationMode={comment.communicationMode}
        />
        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-card__replies">
            {comment.replies.map(reply => (
              <ReplyComponent
                key={reply.id}
                reply={{
                  ...reply,
                  isEditing: isEditing(reply.id)
                }}
                currentUserEmail={loggedInUserEmail}
                menuOptions={getReplyOptions(reply)}
                onOptionClick={onReplyAction}
                onTextEdit={(
                  replyId: string,
                  newText: string,
                  mentions?: CommentMention[],
                  attachments?: (CommentAttachment | File)[]
                ) => onTextEdit(replyId, newText, mentions, attachments)}
                onEditStart={onEditStart}
                onEditComplete={(
                  replyId: string,
                  finalText: string,
                  wasChanged: boolean
                ) => onEditCancel(replyId)}
                onEditCancel={onEditCancel}
                inputConfig={inputConfig}
                iconOverrides={iconOverrides}
                showEditedAt={showEditedAt}
                submitOnBlur={submitOnBlur}
                communicationMode={reply.communicationMode}
              />
            ))}
          </div>
        )}
        {allowReplies && (
          <div className="comment-card__reply-input">
            <ReplyInput
              value={replyValue}
              onChange={handleReplyChange}
              onSubmit={handleReplySubmit}
              placeholder="@mention or reply"
              submitOnBlur={submitOnBlur}
              onClearReply={
                onClearReply ? () => onClearReply(comment.id) : undefined
              }
              {...inputConfig}
            />
          </div>
        )}
      </div>
    );
  }
);

CommentCardComponent.displayName = 'CommentCard';

export const CommentCard = memo(CommentCardComponent);
